<template>
  <div class="w-full mb-12 flex overflow-x-scroll flex-nowrap justify-between items-stretch px-4 xl:px-0 summary-container ">
    <div
      class="blue-gradient dark-transparent px-4 w-full md:w-3/12 flex items-center justify-center shadow-lg rounded-3xl mr-2 cursor-pointer"
      @click="setActive('ALL')"
    >
      <div class="text-center">
        <h6 class="text-white text-lg mb-2 whitespace-nowrap">Success Rate</h6>
        <h1 class="success-rate font-black text-white">{{successRate}}</h1>
        <h3 class="bg-white text-bs-blue-dark px-3 py-1 rounded-full mt-4">{{formatCurrency(equity)}}</h3>
      </div>
    </div>
    <status-card title="Wins" @click="setActive('SUCCESS')"  :number="summary.wins" :class="['mx-2 wins', active && active !== 'SUCCESS' ? 'opacity-10': '']" :arc="pieBits[0]" :bars="bars[0]" />
    <status-card title="Half wins" @click="setActive('HALFWIN')"  :number="summary.halfWins" :class="['mx-2 halfwins', active && active !== 'HALFWIN' ? 'opacity-10': '']"  :arc="pieBits[1]" :bars="bars[1]" />
    <status-card title="Voids" @click="setActive('VOID')"  :number="summary.voids" :class="['mx-2 voids', active && active !== 'VOID' ? 'opacity-10': '']"  :arc="pieBits[2]" :bars="bars[2]"/>
    <status-card title="Half losses" @click="setActive('HALFLOSS')"   :number="summary.halfLosses" :class="['mx-2 halflosses', active && active !== 'HALFLOSS' ? 'opacity-10': '']" :arc="pieBits[3]" :bars="bars[3]"/>
    <status-card title="Losses" @click="setActive('FAIL')"  :number="summary.losses" :class="['mx-2 lg:mr-0 losses', active && active !== 'FAIL' ? 'opacity-10': '']" :arc="pieBits[4]" :bars="bars[4]"/>
    <div class="mobile-status-spacer  lg:hidden"></div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import StatusCard from '../atoms/StatusCard.vue';
import { pie } from 'd3'
import { format } from 'date-fns';

const pieFn = pie()
.value(function(d) { return d.value; })
.sort(null)
export default {
  components: {
    StatusCard,
  },
  data: function() {
    return  {
      active: undefined
    }
  },
  computed: {
    ...mapGetters(["summary","tips", 'equityByDate','settings']),
    ...mapState({
      accaHistory: (state) => state.accumulators.history,
    }),
    currency: function (){
			return this?.settings?.currencyCode || 'EUR'
		},
    accaWins: function() {
      return (this.accaHistory || []).filter(a => a.status === 1).length;
    },
    accaLosses: function() {
      return (this.accaHistory || []).filter(a => a.status === 2).length;
    },
    stakePct: function() {
      return (this.settings && this.settings.stakeAmount || 0) / 100;
    },
    successRate: function() {
      let wins = (this.summary && this.summary.wins) || 0;
      let losses = (this.summary && this.summary.losses) || 0;
      let halfWin = (this.summary && this.summary.halfWins) || 0;
      let halfLoss = (this.summary && this.summary.halfLosses) || 0;
      let totalWins = wins + halfWin + this.accaWins;
      let totalSettled = halfLoss + losses + wins + halfWin + this.accaWins + this.accaLosses;
      let res = ((totalWins / totalSettled) * 100).toFixed(2)

      if (isNaN(res)){
        res = 0
      }
      return res + "%"
    },
    accaOffset: function() {
      // Cumulative profit/loss from settled accas, applied chronologically with stake = stakePct of running equity
      const settled = (this.accaHistory || [])
        .filter(a => (a.status === 1 || a.status === 2) && a.settledDate)
        .map(a => ({
          settledAt: new Date(a.settledDate).getTime(),
          status: a.status,
          odds: Number(a.effectiveOdds != null ? a.effectiveOdds : a.totalOdds),
        }))
        .sort((a, b) => a.settledAt - b.settledAt);

      // Walk tips + accas chronologically — same logic as Chart.vue / equityByDate.
      // Tip outcomes are computed from status+stake+odds because the stored
      // virtualEquityAfterTip is the at-creation snapshot and isn't updated on win.
      const settlementDelta = (status, stake, odds) => {
        if (!stake || !odds) return 0;
        switch ((status || "").toUpperCase()) {
          case "SUCCESS":
          case "WIN":      return stake * (odds - 1);
          case "FAIL":
          case "LOSS":     return -stake;
          case "VOID":     return 0;
          case "HALFWIN":  return (stake / 2) * (odds - 1);
          case "HALFLOSS": return -stake / 2;
          default:         return 0;
        }
      };

      const tipPoints = (this.tips || [])
        .filter(t => t.status !== 'PENDING')
        .map(t => ({
          ts: new Date(t.createdDate).getTime(),
          type: 'tip',
          stake: parseFloat(t.virtualTipAmount) || 0,
          odds: parseFloat(t.oddValue) || 0,
          status: t.status,
        }));
      const accaPoints = settled.map(a => ({ ts: a.settledAt, type: 'acca', status: a.status, odds: a.odds }));
      const merged = [...tipPoints, ...accaPoints].sort((a, b) => a.ts - b.ts);
      let offset = 0;
      let lastEquity = (this.settings && this.settings.startingEquity) || 0;
      for (const e of merged) {
        if (e.type === 'tip') {
          lastEquity += settlementDelta(e.status, e.stake, e.odds);
        } else {
          const stake = lastEquity * this.stakePct;
          const result = e.status === 1 && e.odds > 0 ? stake * (e.odds - 1) : -stake;
          offset += result;
          lastEquity += result;
        }
      }
      return offset;
    },
    equity: function(){
      const tipEquity = this.equityByDate[this.equityByDate.length - 1]?.y || 0;
      return tipEquity + this.accaOffset;
    },
    pieBits: function(){
      
      let {wins, losses,voids, halfWins, halfLosses} = this.summary
      return pieFn([
        { value:wins }, 
        {value: halfWins}, 
        {value: voids}, 
        {value: halfLosses},
        {value:losses}
      ])
    },
    bars: function(){
      let buckets = {}
      this.tips.forEach(({createdDate, status} )=> {
         let d = format(new Date(createdDate), 'yyyy-MM-dd')
         if (!buckets[d]){
           buckets[d] = {'FAIL':0 , 'SUCCESS':0, 'VOID':0, 'HALFWIN':0, 'HALFLOSS': 0, 'PENDING':0}
         }
         buckets[d][status.toString()?.toUpperCase()]++
      })
      let days = Object.keys(buckets).sort()
      
      return ['SUCCESS', 'HALFWIN' , 'VOID', 'HALFLOSS','FAIL','PENDING'].map(item => {
        let bars = []
        days.forEach(day => {
          let total = Object.values(buckets[day]).reduce((res,item) => res+item, 0);
          if (total){
            bars.push({day, value: (buckets[day][item]/total)*100})
          } else {
            bars.push({day, value:0})
          }
        })
        return bars;
      })
    }
  },
  methods: {
    setActive: function(which){
      if (this.active === which){
        this.active = undefined
      } else {
        this.active = which
      }
      this.$emit('on-active', this.active)
    },
    formatCurrency: function (n) {
			
			return (
				typeof n !== "undefined" &&
				n.toLocaleString('en-GB', {
					style: "currency",
					currency: this.currency,
					currencyDisplay: "symbol",
				})
			);
		}
  }
};
</script>

<style>
.success-rate {
    @apply text-4xl;
}
.mobile-status-spacer {
  min-width:0.5rem;
}
.summary-container {
  -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

}
.summary-container::-webkit-scrollbar {
    display: none;
  }
</style>
